export const projects = [
    {
        id: "sidreasoner-recsys",
        name: "Beyond Accuracy in SIDReasoner: Reward, Scale, and Diversity",
        description: "Reproducibility study and extension of SIDReasoner, a generative recommender that reasons over Semantic IDs using LLM and GRPO reinforcement learning. UvA master's research paper.",
        longDescription: `
        **Beyond Accuracy in SIDReasoner** is a reproducibility study and extension of SIDReasoner (*Reasoning over Semantic IDs Enhances Generative Recommendation*), written as a research paper for the **Recommender Systems course** at the **University of Amsterdam (UvA)**.

        SIDReasoner treats sequential recommendation as a reasoning plus Semantic-ID (SID) generation task: a user's history is encoded as a prompt, the model reasons, then emits a hierarchical three-token Semantic ID naming the next item. It's trained in three stages and fine-tuned with GRPO reinforcement learning.

        Our study combines artifact-level evaluation of released models with partial independent retraining and targeted extensions, structured around five research questions:

        - **RQ1 (Reproduction)**: Do released checkpoints recover reported trends? Partially reproducible.
        - **RQ2 (Components)**: Are RL and the reasoning scaffold strictly necessary? **RL is necessary**; explicit reasoning is only conditionally beneficial.
        - **RQ3 (GRPO Reward)**: Is the reward formulation a sensitive design choice? **Reward shape is immaterial**, a gauge degree of freedom of GRPO's advantage normalization.
        - **RQ4 (Model Scale)**: How does backbone size affect performance? Method holds at smaller scale but gains diminish with less LM capacity (Qwen3-0.6B / 1.7B / 4B).
        - **RQ5 (Diversity)**: How do decoding strategies affect item exposure diversity? Clear inference-time-controllable accuracy-diversity trade-off.

        Key finding: performance gains stem primarily from the RL stage, not from reward formulation or reasoning scaffolds. Future work should prioritize robust representation alignment and recommendation-coupled training objectives.

        I worked on this project along with [Edoardo Vergnano](https://www.linkedin.com/), [Daniel Sleiman](https://www.linkedin.com/), and [Xanthos Kekkos](https://www.linkedin.com/).
        `,
        state: "Completed",
        startDate: "April 2026",
        endDate: "June 2026",
        languages: ["Python", "PyTorch", "Hugging Face", "GRPO", "Qwen3", "Slurm"],
        categories: ["Research", "Recommender Systems", "Reinforcement Learning", "NLP", "Machine Learning"],
        availability: "Public",
        link: "https://github.com/SIDReasoner-UvA/sid-reasoner-extension-uva",
    },
    /*
    {
        id: "chill-bill",
        name: "Chill-Bill - Your Smart Payment Assistant",
        description: "AI-powered multimodal payment assistant built with Claude, AWS Transcribe, and the bunq API.",
        longDescription: `
        Chill Bill is an AI-powered multimodal payment assistant.
        It is an hackathon project built for the **bunq Hackathon 2026**, along with [Alexandru Mihai](https://www.linkedin.com/in/alexandru-mihai-790276252/), [Julia Eva Belloni](https://www.linkedin.com/in/julia-eva-belloni-91b2b9332/), and [Gabriela Chavgova](https://www.linkedin.com/in/gabriela-chavgova-4a914bb9/).
        
        It lets users initiate payments using voice recordings, images, PDFs, or plain text.
        The app uses Claude's vision and language capabilities to automatically extract payment details such as IBAN, amount, and recipient name from any input modality.

        The backend is a FastAPI server that orchestrates requests between Claude (extraction), AWS Transcribe (speech-to-text), and the bunq API (payment execution).
        The Android frontend, built in Kotlin with Jetpack Compose, guides the user through a chat-style interface into a review screen before confirming the payment.

        Features include fuzzy contact matching for IBAN lookup, multi-account selection, real-time audio transcription, and document parsing from invoices or receipts.
        `,
        state: "Completed",
        startDate: "April 2026",
        endDate: "April 2026",
        languages: ["Python", "Kotlin", "FastAPI", "Claude Console", "AWS"],
        categories: ["AI Agents", "Mobile", "Hackathon"],
        availability: "Public",
        link: "https://github.com/bunq-hackathon-2026/chill-bill",
        gallery: [
            "/images/projects/chillbill/chillbill-logo.png",
            "/images/projects/chillbill/chillbill1.jpg"
        ]
    },
    */
    {
        id: "franka-rl-ros2",
        name: "Reinforcement Learning Manipulator ROS2",
        description: "A ROS2 Gazebo implementation for simulating and training a Franka manipulator arm using reinforcement learning.",
        longDescription: `
        This project integrates **ROS 2**, **Gazebo**, and **Stable-Baselines3** to create a training pipeline for the **Franka Emika Panda** arm.

        **Key Components:**
        - **Gazebo**: Physics engine for realistic robot dynamics and sensor simulation.
        - **ROS 2**: Middleware for standardized robot communication and control.
        - **Franka Arm**: A 7-DOF industrial collaborative manipulator with an optional gripper.
        - **RL Framework**: Integration with **Gymnasium** and **Stable-Baselines3** for training autonomous agents. At this stage of development, only distance-based reward is being implemented.

        **Core Features:**
        - Full physics simulation including gravity and collision detection.
        - ROS 2 control interface supporting position, velocity, and impedance control.
        - Pre-configured **Docker** environment for setting up across different systems.
        - RViz integration for real-time visualization.
        
        The project serves as a platform for exploring robotic manipulation tasks such as reaching, grasping, and assembly implementing reinforcement learning algorithms.
        `,
        state: "In Progress",
        startDate: "April 2026",
        endDate: "Present",
        languages: ["Python", "ROS2", "Docker", "Gazebo"],
        categories: ["Robotics", "Reinforcement Learning", "Simulation", "Machine Learning"],
        availability: "Private",
        link: "",
    },
    {
        id: "ing-risk-assessment",
        name: "ESG Risk Assessment Platform",
        description: "AI-powered ESG risk assessment platform for automated extraction and analysis of sustainability data from corporate reports. Implemented a full-stack solution (FastAPI + React) with LLM-based vision and text processing to extract emissions metrics, targets, and assurance data from complex PDFs.",
        longDescription: `AI-powered platform for automated extraction, analysis, and visualization of Environmental, Social, and Governance (ESG) data from corporate annual and sustainability reports.
        The system uses a full-stack architecture (FastAPI backend and React frontend) and advanced LLM-based document understanding, including vision-based PDF analysis, to accurately extract emissions metrics, targets, and assurance information from complex reports.
        It supports batch processing, structured validation, trend analysis, and risk assessment to inform cooperation or suspension decisions for financial institutions.
        The project also explores alternative extraction techniques (semantic chunking with local LLMs and high-resolution document parsing) to evaluate trade-offs between accuracy, cost, and performance.

        This was developed during the ING's Global Hackathon 2026 (Student Track) along with [Martina Vasileva](https://www.linkedin.com/in/martina-vasileva-1425a4272/), [Vasilisa Soboleva](https://www.linkedin.com/in/vasilisa-soboleva/), [Cagan Oflazoglu](https://www.linkedin.com/in/cagan-oflazoglu/), and [Luca Giandomenico](https://www.linkedin.com/in/lgiandomenico/).
        `,
        state: "Completed",
        startDate: "February 2026",
        endDate: "February 2026",
        languages: ["Python", "React", "LangChain", "Ollama", "OpenAI", "FastAPI"],
        categories: ["AI Agents", "Data Extraction", "Data Visualization", "Dashboard", "Hackathon"],
        availability: "Public",
        link: "https://github.com/ING-hackathon-org/ING_FinalRepository",
        gallery: [
            "/images/projects/ing/img1.png",
            "/images/projects/ing/img2.png",
            "/images/projects/ing/img3.png",
            "/images/projects/ing/img4.png"
        ]
    },
    /*
    {
        id: "folio",
        name: "Folio - Document Storage",
        description: "A centralized, self-hosted system for managing signed documents, certificates, and contracts. The system works by storing metadata (dates, issuers, categories) in PostgreSQL while keeping the actual files in a structured directory.",
        longDescription: `A modern, self-hosted document management system designed for organizing and tracking important documents, certificates, and contracts. Folio provides document organization with automated status tracking, and a responsive interface.
        The system works by storing metadata (dates, issuers, categories) in PostgreSQL while keeping the actual files in a structured directory.
        
        **Core Features**  
        - **AI-Powered Field Suggestions**: Intelligent document analysis using local LLMs to automatically suggest metadata fields (category, issuer, dates, description) when uploading documents, with full user control to review and modify suggestions before saving
        - **Smart Status Tracking**: Automatic document validity calculation (Valid, Expiring Soon, Expired, Permanent)
        - **Dashboard Overview**: Quick snapshot with expiring documents, analytics, and recent activity
        - **Advanced Search & Filtering**: Full-text search, filter by category/issuer/dates, smart sorting
        - **Drag-and-Drop Upload**: Modern upload interface with metadata management and duplicate detection
        `,
        state: "Completed",
        startDate: "February 2026",
        endDate: "Present",
        languages: ["Python", "Typescript", "React", "Ollama", "LangChain", "PostgreSQL", "FastAPI"],
        categories: ["AI Agents", "Document Management", "Dashboard"],
        availability: "Public",
        link: "https://github.com/SalvatoreLS/folio-document-storage",
        gallery: [
            "/images/projects/folio/folio1.jpg",
            "/images/projects/folio/folio2.png",
            "/images/projects/folio/folio3.png",
        ]
    },
    */
    {
        id: "fact-ai",
        name: "Reproduction and Extension of 'Towards Safer Pretraining' by Microsoft",
        description: "Academic group project which reproduced a safety-oriented dataset analysis pipeline for LLM pretraining using open-weight models. The TTP framework and safety taxonomy were benchmarked across open-source architectures, applied to major web-scale datasets, and evaluated for toxicity leakage and deployment costs.",
        longDescription: `This project reproduced and evaluated a safety-focused dataset analysis pipeline for large-language-model pretraining using open-weight models. We implemented the proposed safety taxonomy and the Topical and Toxic Prompt (TTP) framework, benchmarking them across multiple open-source architectures to identify cost-effective alternatives to proprietary models.

        The best-performing model was then applied to large-scale web datasets, including Common Crawl, C4, and FineWeb, to analyze topical coverage and the prevalence of harmful content. In addition, the HAVOC benchmark was reproduced to measure toxicity leakage under harmful prompting. The study also assessed the computational and environmental costs of deploying such safety pipelines at scale.

        The project culminated in the production of a research paper, which was presented to the teaching committee.

        I worked on this project along with [Niccolò Caselli](https://www.linkedin.com/in/caselli-niccolo/), [Francesco Massafra](https://www.linkedin.com/in/francesco-massafra-993097216/), and [Samuele Punzo](https://www.linkedin.com/in/samuele-punzo/).
        `,
        state: "Completed",
        startDate: "January 2026",
        endDate: "January 2026",
        languages: ["Python", "Ollama", "LangChain", "Hugging Face"],
        categories: ["Research", "NLP", "Machine Learning", "Content Filtering", "Web Content"],
        availability: "Public",
        link: "https://github.com/NiccoloCase/safer-pretraining-reproduction-uva"
    },
    /*
    {
        id: "pizzeria-handler",
        name: "NonnaOps - Autonomous Restaurant Operating System",
        description: "Autonomous restaurant operating system built for the Prosus Spring Hackathon 2026. Manages a 30-day restaurant simulation using a hybrid LLM + deterministic architecture to handle cash flow, inventory, staffing, and supplier crises.",
        longDescription: `
        **NonnaOps** is an autonomous restaurant operating system developed for the **Prosus Spring Hackathon 2026**, designed to simulate and manage the operations of "Trattoria La Bancarotta" over a 30-day period.

        The system acts as an AI-powered restaurant owner, monitoring cash flow, inventory, suppliers, staffing, demand, and service quality in real-time to prevent business collapse.

        **Architecture:**
        The core innovation is a **hybrid model** that combines strategic LLM reasoning with deterministic safety checks — preventing reckless autonomous decisions while maintaining adaptability:
        - **LLM Component**: Strategic planning, regime classification, and reasoning over trade-offs.
        - **Deterministic Layer**: Forecasting, inventory logic, safety checks, and action generation.

        **Key Components:**
        - **Runner**: Single-game runtime managing the full simulation loop.
        - **Hybrid Planner**: Primary compiler agent that classifies business regimes and dispatches actions.
        - **Memory-ReAct Agent**: LLM agent with memory-backed reasoning for contextual decision-making.
        - **Evaluation Harness**: Batch execution and observability for experiment tracking.

        The system integrates with the RestBench simulator API and optionally persists experiment data in MongoDB.

        I worked on this project along with [Niccolò Caselli](https://www.linkedin.com/in/caselli-niccolo/), [Francesco Massafra](https://www.linkedin.com/in/francesco-massafra-993097216/), and [Vlad Postoroanca](https://www.linkedin.com/in/vlad-postoroanca/).
        `,
        state: "Completed",
        startDate: "May 2026",
        endDate: "May 2026",
        languages: ["Python", "Claude API", "MongoDB"],
        categories: ["AI Agents", "Simulation", "Hackathon"],
        availability: "Public",
        link: "https://github.com/spring-hackathon-prosus/pizzeria-handler",
    },
    */
    /*
    {
        id: "sported",
        name: "Sported - AI-powered Matchmaker",
        description: "AI-powered platform designed to solve the organization of sports matches. It finds compatible players in a specific area using geolocation clustering and uses AI agents to coordinate matches.",
        longDescription: `
        Sported is an AI-powered platform designed to solve the **organization** of sports matches. Instead of endless group chats, the system automates the entire process:
        - **Matchmaking**: It finds compatible players in a specific area using geolocation clustering.
        - **Organization**: It creates balanced teams based on skill levels and preferences.
        - **Scheduling**: It handles the logistics of time and location automatically.
        
        This project has been developed during the **Prosus AI Hackathon 2025**.
        **Architecture**: We built it as a monorepo containing a React Native (Capacitor) mobile client, a Node.js/Express backend, and the Python-based AI agent service.

        I was responsible for the implementation of the interaction between agents and the interaction with the backend.

        The system functions through a specific workflow:
        - **Clustering**: First, we use K-means algorithms to group users geographically based on their latitude and longitude.
        - **Coordinator Agent**: This is the main "brain" I implemented. It acts as the orchestrator that selects compatible players from a cluster and validates match plans.
        - **User Agents**: To make the interaction seamless, the system spawns individual "User Agents" that represent specific players. These agents answer questions regarding availability and preferences on behalf of the user.
        - **Execution**: Once the Coordinator and User Agents align, the system communicates with the Server API to write the match record and trigger push notifications to the actual human users.
        `,
        state: "Completed",
        startDate: "November 2025",
        endDate: "November 2025",
        languages: ["Python", "MongoDB", "Node.js", "Express", "AI Agents"],
        categories: ["AI Agents", "Matchmaker", "Sports", "Hackathon"],
        availability: "Public",
        link: "https://github.com/DuckBugging-prosusHackathon/Sported",
        gallery: [
            "/images/projects/sported/agent_architecture.png"
        ]
    },
    */
    {
        id: "isaac-assistant",
        name: "Isaac Assistant",
        description: "Application for assisting user in the game The Binding of Isaac, using computer vision for inference and hashing for computing recipes. It can be used to find optimal recipes and suggest items to the player.",
        longDescription: `
        The **Isaac Assistant** is a **C++/Java** application designed to enhance the gameplay experience of "The Binding of Isaac".
        Two versions of the assistant have been developed: a **Java version**, using **ModelZoo** for object detection, and a **C++ version** leveraging **OpenCV** and **LibTorch** for real-time image processing.
        I developed two versions because many incompatibilities were found with YOLO model in both languages, so I wanted to explore different approaches, and practice both C++ and Java.
        
        Two datasets have been created specifically for this project, one for identifying **player's items** and another for recognizing **available items on the floor**.
        These datasets have been labeled using **Roboflow**, and custom **YOLO models** have been trained to achieve high accuracy in item detection.

        The assistant functions by capturing the game screen and processing the images to detect and identify items using the trained models.
        Afterwards, it computes optimal item combinations and recipes using a **hashing algorithm**, providing **real-time suggestions** to the player, sorting items by their rarity and effectiveness.
        
        I decided to implement this project to deepen my understanding of **computer vision techniques** and **real-time image processing**, while also creating a useful tool for gamers.
    `,
        state: "In Progress",
        startDate: "October 2025",
        endDate: "Present",
        languages: ["C++", "OpenCV", "LibTorch", "YOLO", "Java", "ModelZoo", "Ultralytics YOLO", "Roboflow"],
        categories: ["Computer Vision", "Machine Learning", "Game Assistant"],
        availability: "Public",
        link: "https://github.com/SalvatoreLS/recipe-suggestor"
    },
    {
        id: "nereo-ros-code",
        name: "Nereo ROV PoliTOcean",
        description: "Development of a real time ROV monitoring and control system using ROS2 and PyQt6. C++ ROS2 nodes handled sensor communication and data acquisition for stable navigation, while a dedicated UDP socket was used for low latency camera streaming, bypassing ROS2 image transport.",
        longDescription: `Design and development of a real time **graphical user interface** for monitoring and control of a remotely operated vehicle using **ROS2** and **PyQt6**.
        The system included custom **C++ ROS2 nodes** for reliable sensor communication and data acquisition, supporting stable navigation and control.
        The overall architecture handled hardware interfacing and communication protocols, while the GUI received the camera stream through a dedicated **UDP socket**.
        This approach bypassed standard **ROS2 image transport** to reduce latency and achieve the **throughput** required for real time operation.

        I implemented the code in team with another team member, while I was part of the PoliTOcean team. The team is still working on it.

        I also implemented a first version of an autonomous version of the ROV, just before leaving the university. Despite it is not too much advanced, it served as a proof of concept and a starting point for future development.
        This version is not included in the repository, but it can be found on this [repository](https://github.com/PoliTOcean/CRAB).

    `,
        state: "Completed",
        startDate: "April 2024",
        endDate: "June 2025",
        languages: ["C++", "ROS2", "PyQt6"],
        categories: ["ROS2", "Robotics", "GUI"],
        availability: "Public",
        link: "https://github.com/PoliTOcean/nereo_ros2_code",
    },
    {
        id: "data-package-organizer",
        name: "Data Package Organizer",
        description: "Tool to organize files into Frictionless Data packages, using Gemini to generate metadata. It can be used to prepare datasets for analysis or sharing.",
        longDescription: `
        The **Datapackage Arranger** is a **Python utility** that automates dataset organization and metadata generation.
        It scans folders or individual files, extracts content from formats such as **PDF, DOCX, TXT, XLSX, CSV, and XML**, and uses **Google Gemini** to analyze these previews and produce structured metadata including descriptions, tags, and schemas.

        Its core components are a **multi-format file parser** that produces representative previews and an **AI agent** that interprets them to generate accurate metadata.
        The tool supports **interactive tag refinement**, basic **data cleaning** for tabular files, and automatic **ZIP extraction**.

        It outputs a custom **datapackage.json** containing file paths, types, tags, descriptions, and schemas, along with a processed directory of cleaned and organized files.
        The project was created to automate dataset curation, reduce manual tagging, and embed AI analysis into data engineering workflows.

        I built this tool to streamline the often tedious process of dataset organization, leveraging **AI** to enhance metadata quality and consistency.
        This tool can be useful as a utility for a **crawler** or **data engineer** looking to quickly prepare datasets for analysis or sharing.

        The source code for this project is not currently public.
        The implementation contains some hardcoded keywords and domain specific heuristics that are coupled to private datasets I worked with.
        While the data itself was not collected illegally, I don't want to disclose the data, or indirect hints about its nature.
    `,
        state: "Completed",
        startDate: "November 2025",
        endDate: "November 2025",
        languages: ["Python"],
        categories: ["AI Agents", "Data Tools"],
        availability: "Private",
        link: ""
    },
    {
        id: "gym-telegram-bot",
        name: "Gym Telegram Bot",
        description: "The gymBot is a Python Telegram bot that manages gym workouts, tracks progression, and provides update suggestions using a state machine with multiple states and substates for handling user interactions.",
        longDescription: `
        The **gymBot** is a **Python Telegram bot** that automates gym workout management, progression tracking, and personalized update suggestions.

        It guides users through authentication, program selection, day planning, and workout execution via interactive messaging, leveraging a **PostgreSQL database** to store user profiles, exercise programs, and performance data.
        The bot analyzes workout history to provide **statistics** and **recommendations** for adjustments.
        Its core components are a **Telegram bot interface** that handles user messages, a **state machine** with multiple main states (e.g., DEAD, LOGIN, AUTHENTICATED, TYPE_PROGRAM, ...)
        and substates for nuanced operations like login steps, set updates, and exercise modifications, and a **database abstraction layer** that manages data persistence and retrieval.

        The tool supports **user registration and login**, **program and day selection**, **real-time workout progression** with set-by-set guidance, **exercise updates** (reps, weight, rest), and basic data validation.
        It outputs personalized workout messages, progress summaries, and database-stored updates, along with a structured program directory of exercises and sets.
        The project was created to automate the often tedious process of gym routine management, reduce manual logging, and embed intelligent suggestions into fitness workflows.
        
        I built this tool to streamline the process of tracking and updating gym workouts, leveraging a **state-driven architecture** to enhance user experience and consistency.
        This tool can be useful as a utility for **fitness enthusiasts** or **trainers** looking to quickly manage and personalize training programs via Telegram.
    `,
        state: "Completed",
        startDate: "April 2025",
        endDate: "May 2025",
        languages: ["Python", "Telegram API", "PostgreSQL"],
        categories: ["Telegram Bot", "Gym"],
        availability: "Public",
        link: "https://github.com/SalvatoreLS/gymBot",
        gallery: [
            "/images/projects/gymbot/bot_architecture.png"
        ]
    },
	/*
    {
        id: "deep-learning-journey",
        name: "Deep Learning Journey",
        description: "This repository documents my learning path in the field of deep learning and machine learning. It includes notes, code snippets and projects as I explore various concepts in neural networks and AI.",
        longDescription: `
        Inspired by the course **'Deep Learning Specialization'** by **Andrew Ng** on Coursera, I created this repository to document my learning journey in deep learning and machine learning.
        The repository includes detailed notes, code snippets, and projects that I have worked on while exploring various concepts in neural networks, optimization algorithms, and AI applications.

        It consists of three sections:
        **1. Deep Learning Specialization**: a collection of manual implementations of the concepts learned in the specialization, including neural networks, convolutional networks, sequence models, and more.
        **2. Twitter Sentiment Analysis**: a project that analyzes Twitter data to classify sentiments using deep learning techniques, without relying on transformers architecture.
        **3. Gesture Recognition (YOLO + PyQt6)**: GUI application that lets users map hand gestures to custom commands and trigger them in real time using a YOLO-based detector. Commands are defined through a settings panel, saved automatically, and reloaded at startup.
    `,
        state: "Completed",
        startDate: "September 2024",
        endDate: "September 2024",
        languages: ["Python", "Ultralytics YOLO", "PyQt6"],
        categories: ["Deep Learning", "Computer Vision", "NLP", "GUI", "Machine Learning"],
        availability: "Private",
        link: ""
    },
    */
    {
        id: "dl1-assignments",
        name: "Deep Learning: From MLP to GNNs and Transformers",
        description: "Solutions to the assignments from the Deep Learning 1 course at the University of Amsterdam (UvA). The assignments spans from MLPs to GNNs and Transformers.",
        longDescription: `
        This collection contains my solutions to the assignments from the **Deep Learning 1 course** at the **University of Amsterdam (UvA)**.
        The assignments cover a range of topics in deep learning, including neural networks, backpropagation, optimization algorithms, and regularization techniques.

        It consists of Jupyter notebooks and Python scripts that implement the concepts learned in the course, along with explanations and visualizations to illustrate the results.

        Two assignments are included:
        **- Assignment 1**: Implements **Multi-Layer Perceptrons (MLPs)** from scratch in NumPy and using PyTorch, trained on the CIFAR-10 dataset.
        **- Assignment 2**: It is composed of three parts. **Part 1** explores **Convolutional Neural Network (CNN)** properties using synthetic datasets, training CNNs with varying architectures and convolution types to analyze performance effects. **Part 2** builds a simplified **GPT-2 model** for character-level text generation, implementing rotary position embeddings, causal self-attention, transformer decoder blocks, and text generation. **Part 3** implements **Graph Neural Networks (GNNs)**, including Graph Convolutional Networks (GCN), Matrix-based GCN, and Graph Attention Networks (GAT), utilizing PyTorch Geometric for graph data handling and providing training scripts with TensorBoard logging.
        **- Assignment 3**: Explores Variational Autoencoders (VAEs) and Adversarial Attacks, implementing a VAE for image generation and adversarial attack techniques to evaluate model robustness.
        
        Code cannot be shared publicly due to university policies.
        `,
        state: "Completed",
        startDate: "October 2025",
        endDate: "December 2025",
        languages: ["Python", "NumPy", "PyTorch", "PyTorch Geometric", "Jupyter", "TensorBoard"],
        categories: ["Deep Learning", "Neural Networks", "GNNs", "Transformers", "Machine Learning", "Research"],
        availability: "Private",
        link: ""
    },
    {
        id: "cv1-assignments",
        name: "Computer Vision: From Color Spaces to CNNs",
        description: "Solutions to the assignments from the Computer Vision 1 course at the University of Amsterdam (UvA). The assignments spans from color spaces, to Optical Flow, to CNNs.",
        longDescription: `
        This collection contains my solutions to the **Computer Vision 1 course** assignments at the **University of Amsterdam (UvA)**.
        The assignments cover fundamental concepts in computer vision, including image processing, feature detection, and object recognition.

        It consists of Jupyter notebooks and Python scripts that implement the concepts learned in the course, along with explanations and visualizations to illustrate the results.

        Two assignments are included:
        **- Assignment 1**: Color spaces, Color constancy, Intrinsic Image Decomposition, Photometric Stereo.
        **- Assignment 2**: Neighborhood Processing, Gaussian Filters, Gabor Filters, Noise Reduction, Edge Detection, Background Separation.
        **- Assignment 3**: Harris Corner Detector, Shi & Tomasi Feature Detection, Optical Flow and Lucas-Kanade Method, Feature Tracking, RAFT Model.
        **- Assignment 4**: Image Alignment, Image Stitching, Automatic Panorama Stitching.
        **- Assignment 5**: Keypoint Detection, Bag of Visual Words, Convolutional Neural Networks.

        Code cannot be shared publicly due to university policies.
        `,
        state: "Completed",
        startDate: "September 2025",
        endDate: "October 2025",
        languages: ["Python", "NumPy", "OpenCV", "Jupyter"],
        categories: ["Computer Vision", "Image Processing", "Feature Detection", "Machine Learning", "Research"],
        availability: "Private",
        link: ""
    },
    {
        id: "pointer-moving",
        name: "Pointer Moving",
        description: "A Python application that moves the mouse pointer based on hand gestures detected through the webcam using computer vision techniques. It implements three interaction modes: moving, clicking, and scrolling.",
        longDescription: `
        The **Pointer Moving** application is a **Python** program that utilizes Mediapipe and OpenCV to detect hand gestures through the webcam and move the mouse pointer accordingly.
        Three interaction modes are implemented:
        - **Moving Mode**: use index and middle fingers to move the pointer.
        - **Clicking Mode**: pinch index and middle fingers to perform a click.
        - **Scrolling Mode**: put index and middle finger together and move up/down to scroll (vertical gradient is computed and when a threshold is exceeded, a scroll event is triggered).

        The application captures video from the webcam, processes each frame to detect hand landmarks using **Mediapipe Hands**, and translates specific gestures into mouse movements and actions using the **PyAutoGUI** library.
        The project was created to explore computer vision techniques and provide an intuitive way to control the mouse pointer using hand gestures.
        An improved version might serve as an accessibility tool or a novel input method for human-computer interaction.

        I built this tool as a first step with real-time gesture recognition and enhance user interaction with the computer through natural movements.
    `,
        state: "Completed",
        startDate: "March 2024",
        endDate: "March 2024",
        languages: ["Python", "Mediapipe", "OpenCV", "PyAutoGUI"],
        categories: ["Computer Vision", "Gesture Recognition"],
        availability: "Private",
        link: ""
    }
];
